import React, { useState, useEffect, useMemo } from 'react';
import { Mail, Calendar, MessageSquare, Eye, Trash2, X, MailOpen } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'lifetime', label: 'Lifetime' }
];

const withinRange = (dateStr, range) => {
  if (range === 'lifetime') return true;
  const d = new Date(dateStr);
  if (isNaN(d)) return true;
  const now = new Date();
  const cutoff = new Date(now);
  if (range === 'week') cutoff.setDate(now.getDate() - 7);
  if (range === 'month') cutoff.setMonth(now.getMonth() - 1);
  return d >= cutoff;
};

const REASON_LABELS = {
  general: 'General', bulk: 'Bulk Order', speaking: 'Speaking Engagement',
  workshop: 'Workshop', media: 'Media Enquiry'
};
const INTEREST_LABELS = {
  'bulk-order': 'Bulk Order', speaking: 'Speaking Engagement', workshop: 'Workshop',
  both: 'Bulk Order & Speaking', general: 'General'
};
const typeLabel = (rec) => {
  if (rec._kind === 'bulk') return INTEREST_LABELS[rec.interest] || rec.interest || 'Enquiry';
  return REASON_LABELS[rec.reason] || rec.reason || 'General';
};

const SubmissionsViewer = () => {
  const [activeTab, setActiveTab] = useState('newsletters');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('lifetime');
  const [typeFilter, setTypeFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const [activeMessage, setActiveMessage] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const tabs = [
    { id: 'newsletters', label: 'Subscribers', icon: Mail },
    { id: 'events', label: 'Event Registrations', icon: Calendar },
    { id: 'contacts', label: 'Messages', icon: MessageSquare }
  ];

  useEffect(() => {
    setSubmissions([]);
    setTypeFilter('all');
    setEventFilter('all');
    setSelectedIds([]);
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      if (activeTab === 'contacts') {
        // Unified inbox: merge contact messages + legacy bulk enquiries
        const [cRes, bRes] = await Promise.all([
          fetch(`${API}/api/admin/submissions/contacts`).then((r) => r.json()),
          fetch(`${API}/api/admin/submissions/bulk`).then((r) => r.json())
        ]);
        const contacts = (cRes.submissions || []).map((s) => ({ ...s, _kind: 'contact' }));
        const bulk = (bRes.submissions || []).map((s) => ({ ...s, _kind: 'bulk' }));
        const merged = [...contacts, ...bulk].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setSubmissions(merged);
      } else {
        const response = await fetch(`${API}/api/admin/submissions/${activeTab}`);
        const data = await response.json();
        if (data.success) setSubmissions(data.submissions);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(
    () => submissions
      .filter((s) => withinRange(s.created_at, range))
      .filter((s) => (activeTab === 'contacts' && typeFilter !== 'all' ? typeLabel(s) === typeFilter : true))
      .filter((s) => (activeTab === 'events' && eventFilter !== 'all' ? (s.event_name || 'General') === eventFilter : true)),
    [submissions, range, typeFilter, eventFilter, activeTab]
  );

  const typeOptions = useMemo(() => {
    if (activeTab !== 'contacts') return [];
    return Array.from(new Set(submissions.map((s) => typeLabel(s)))).sort();
  }, [submissions, activeTab]);

  const eventOptions = useMemo(() => {
    if (activeTab !== 'events') return [];
    return Array.from(new Set(submissions.map((s) => s.event_name || 'General'))).sort();
  }, [submissions, activeTab]);

  const exportCSV = () => {
    if (!filtered.length) return;
    const cols = Array.from(
      filtered.reduce((set, r) => {
        Object.keys(r).forEach((k) => { if (k !== 'id' && k !== '_kind') set.add(k); });
        return set;
      }, new Set())
    );
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const lines = [cols.join(',')].concat(
      filtered.map((r) => cols.map((c) => esc(r[c])).join(','))
    );
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}-${range}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setRead = async (id, read) => {
    try {
      await fetch(`${API}/api/admin/submissions/contacts/${id}/read`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ read })
      });
      setSubmissions((prev) => prev.map((s) => (s.id === id ? { ...s, read } : s)));
      setActiveMessage((prev) => (prev && prev.id === id ? { ...prev, read } : prev));
    } catch (e) {
      console.error('Failed to update read status', e);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    try {
      await fetch(`${API}/api/admin/submissions/contacts/${id}`, { method: 'DELETE' });
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      if (activeMessage && activeMessage.id === id) setActiveMessage(null);
    } catch (e) {
      console.error('Failed to delete message', e);
    }
  };

  const deleteSubscriber = async (id) => {
    if (!window.confirm('Delete this subscriber? This cannot be undone.')) return;
    try {
      await fetch(`${API}/api/admin/submissions/newsletters/${id}`, { method: 'DELETE' });
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      setSelectedIds((prev) => prev.filter((x) => x !== id));
    } catch (e) {
      console.error('Failed to delete subscriber', e);
    }
  };

  const bulkDeleteSubscribers = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected subscriber${selectedIds.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    try {
      await fetch(`${API}/api/admin/submissions/newsletters/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds })
      });
      const removed = new Set(selectedIds);
      setSubmissions((prev) => prev.filter((s) => !removed.has(s.id)));
      setSelectedIds([]);
    } catch (e) {
      console.error('Failed to bulk delete subscribers', e);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openMessage = (sub) => {
    setActiveMessage(sub);
    if ('read' in sub && !sub.read) setRead(sub.id, true);
  };

  const statusBadge = (status) => {
    const isUnsub = status === 'unsubscribed';
    return (
      <span style={{
        padding: '4px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600',
        backgroundColor: isUnsub ? '#f0f0f0' : '#e6f7e6', color: isUnsub ? '#999999' : '#00a000'
      }}>
        {isUnsub ? 'Unsubscribed' : 'Subscribed'}
      </span>
    );
  };

  const readBadge = (read) => (
    <span style={{
      padding: '4px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600',
      backgroundColor: read ? '#f0f0f0' : '#FDF0D5', color: read ? '#999999' : '#9A7B2E'
    }}>
      {read ? 'Read' : 'New'}
    </span>
  );

  const typeBadge = (rec) => (
    <span style={{
      padding: '4px 12px', borderRadius: '12px', fontSize: '0.78rem', fontWeight: '600',
      backgroundColor: '#F1ECDD', color: '#9A7B2E', whiteSpace: 'nowrap'
    }}>
      {typeLabel(rec)}
    </span>
  );

  const th = { padding: '16px', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', color: '#666666', textTransform: 'uppercase' };
  const td = { padding: '16px' };

  const renderRow = (sub) => {
    switch (activeTab) {
      case 'newsletters':
        return (
          <tr key={sub.id} data-testid={`sub-row-${sub.id}`} style={{ borderBottom: '1px solid #F0F0F0', backgroundColor: selectedIds.includes(sub.id) ? '#FBF7EC' : 'transparent' }}>
            <td style={{ ...td, width: '40px' }}>
              <input type="checkbox" checked={selectedIds.includes(sub.id)} onChange={() => toggleSelect(sub.id)} data-testid={`sub-select-${sub.id}`} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
            </td>
            <td style={td}>{sub.first_name || ''}</td>
            <td style={td}>{sub.email}</td>
            <td style={td}>{statusBadge(sub.status)}</td>
            <td style={td}>{new Date(sub.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>
              <button onClick={() => deleteSubscriber(sub.id)} data-testid={`sub-delete-${sub.id}`} title="Delete subscriber" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#cc0000', padding: '4px' }}><Trash2 size={18} /></button>
            </td>
          </tr>
        );
      case 'events':
        return (
          <tr key={sub.id} data-testid={`event-reg-row-${sub.id}`} style={{ borderBottom: '1px solid #F0F0F0' }}>
            <td style={td}>{sub.name}</td>
            <td style={td}>{sub.email}</td>
            <td style={td}>{sub.organisation || '-'}</td>
            <td style={td}>{sub.event_name || 'General'}</td>
            <td style={td}>{new Date(sub.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
          </tr>
        );
      case 'contacts': {
        const isContact = sub._kind === 'contact';
        const unread = isContact && !sub.read;
        return (
          <tr key={sub.id} data-testid={`contact-row-${sub.id}`} style={{ borderBottom: '1px solid #F0F0F0', fontWeight: unread ? '600' : '400' }}>
            <td style={td}>{isContact ? readBadge(sub.read) : <span style={{ color: '#CCCCCC' }}>—</span>}</td>
            <td style={td}>{typeBadge(sub)}</td>
            <td style={td}>{sub.name}</td>
            <td style={td}>{sub.email}</td>
            <td style={{ ...td, maxWidth: '260px', color: '#666666', fontWeight: '400' }}>
              {(sub.message || '').length > 70 ? sub.message.slice(0, 70) + '…' : (sub.message || <span style={{ color: '#BBBBBB' }}>—</span>)}
            </td>
            <td style={td}>{new Date(sub.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>
              <button onClick={() => openMessage(sub)} data-testid={`view-message-${sub.id}`} title="View" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#111111', padding: '4px' }}><Eye size={18} /></button>
              {isContact && (
                <button onClick={() => deleteContact(sub.id)} data-testid={`delete-message-${sub.id}`} title="Delete" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#cc0000', padding: '4px' }}><Trash2 size={18} /></button>
              )}
            </td>
          </tr>
        );
      }
      default:
        return null;
    }
  };

  const renderHeaders = () => {
    switch (activeTab) {
      case 'newsletters': {
        const allSelected = filtered.length > 0 && filtered.every((s) => selectedIds.includes(s.id));
        const toggleAll = () => {
          if (allSelected) {
            const ids = new Set(filtered.map((s) => s.id));
            setSelectedIds((prev) => prev.filter((x) => !ids.has(x)));
          } else {
            setSelectedIds((prev) => Array.from(new Set([...prev, ...filtered.map((s) => s.id)])));
          }
        };
        return (
          <tr style={{ borderBottom: '2px solid #E0E0E0' }}>
            <th style={{ ...th, width: '40px' }}><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="sub-select-all" style={{ width: '16px', height: '16px', cursor: 'pointer' }} /></th>
            <th style={th}>First Name</th><th style={th}>Email</th><th style={th}>Status</th><th style={th}>Subscribed</th><th style={th}>Actions</th>
          </tr>
        );
      }
      case 'events':
        return (<tr style={{ borderBottom: '2px solid #E0E0E0' }}><th style={th}>Name</th><th style={th}>Email</th><th style={th}>Organisation</th><th style={th}>Event</th><th style={th}>Registered</th></tr>);
      case 'contacts':
        return (<tr style={{ borderBottom: '2px solid #E0E0E0' }}><th style={th}>Status</th><th style={th}>Type</th><th style={th}>Name</th><th style={th}>Email</th><th style={th}>Message</th><th style={th}>Received</th><th style={th}>Actions</th></tr>);
      default:
        return null;
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: '700', margin: 0 }}>Form Submissions</h1>
        <button onClick={exportCSV} disabled={!filtered.length} data-testid="export-csv-button"
          style={{ padding: '10px 18px', backgroundColor: filtered.length ? '#111111' : '#CCCCCC', color: '#F4F1E8', border: 'none', borderRadius: '4px', fontSize: '0.9rem', fontWeight: '600', cursor: filtered.length ? 'pointer' : 'not-allowed' }}>
          Export CSV
        </button>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '2px solid #E0E0E0' }}>
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: 'transparent', border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid #C9A24B' : '3px solid transparent',
              color: activeTab === tab.id ? '#9A7B2E' : '#666666', fontSize: '0.95rem', fontWeight: activeTab === tab.id ? '600' : '500', cursor: 'pointer', transition: 'all 0.2s ease' }}>
            <tab.icon size={18} />{tab.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <span style={{ fontSize: '0.85rem', color: '#666666', fontWeight: '600' }}>Show:</span>
        {RANGES.map((r) => (
          <button key={r.id} onClick={() => setRange(r.id)} data-testid={`range-${r.id}`}
            style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer',
              border: range === r.id ? '1px solid #111111' : '1px solid #E0E0E0',
              backgroundColor: range === r.id ? '#111111' : '#FFFFFF', color: range === r.id ? '#F4F1E8' : '#666666' }}>
            {r.label}
          </button>
        ))}
        {activeTab === 'contacts' && typeOptions.length > 0 && (
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="type-filter"
            style={{ marginLeft: '8px', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '600', border: '1px solid #E0E0E0', backgroundColor: '#FFFFFF', color: '#111111', cursor: 'pointer' }}>
            <option value="all">All types</option>
            {typeOptions.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        )}
        {activeTab === 'events' && eventOptions.length > 0 && (
          <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} data-testid="event-filter"
            style={{ marginLeft: '8px', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '600', border: '1px solid #E0E0E0', backgroundColor: '#FFFFFF', color: '#111111', cursor: 'pointer' }}>
            <option value="all">All events</option>
            {eventOptions.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        )}
        {activeTab === 'newsletters' && selectedIds.length > 0 && (
          <button onClick={bulkDeleteSubscribers} data-testid="bulk-delete-subscribers-button"
            style={{ marginLeft: '8px', display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', border: 'none', backgroundColor: '#cc0000', color: '#FFFFFF' }}>
            <Trash2 size={16} /> Delete selected ({selectedIds.length})
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#999999' }} data-testid="submissions-count">
          {filtered.length} {filtered.length === 1 ? 'record' : 'records'}
        </span>
      </div>

      <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', overflowX: 'auto' }}>
        {loading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#666666' }}>Loading submissions...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#666666' }}>No records for this period.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>{renderHeaders()}</thead>
            <tbody>{filtered.map((sub) => renderRow(sub))}</tbody>
          </table>
        )}
      </div>

      {activeMessage && (
        <div data-testid="message-modal" onClick={() => setActiveMessage(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 2000, backgroundColor: 'rgba(17,17,17,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', maxWidth: '620px', width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '24px 28px', borderBottom: '1px solid #F0F0F0' }}>
              <div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: '600', margin: 0, color: '#111111' }}>{activeMessage.name}</h2>
                <a href={`mailto:${activeMessage.email}`} style={{ color: '#9A7B2E', fontSize: '0.95rem', fontWeight: '600' }}>{activeMessage.email}</a>
              </div>
              <button onClick={() => setActiveMessage(null)} data-testid="message-modal-close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#666666' }}><X size={22} /></button>
            </div>
            <div style={{ padding: '24px 28px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px', fontSize: '0.85rem', color: '#666666' }}>
                {activeMessage.reason && <span><strong style={{ color: '#111111' }}>Reason:</strong> {activeMessage.reason}</span>}
                {activeMessage.interest && <span><strong style={{ color: '#111111' }}>Interest:</strong> {activeMessage.interest}</span>}
                {activeMessage.organisation && <span><strong style={{ color: '#111111' }}>Organisation:</strong> {activeMessage.organisation}</span>}
                {activeMessage.quantity && <span><strong style={{ color: '#111111' }}>Quantity:</strong> {activeMessage.quantity}</span>}
                <span><strong style={{ color: '#111111' }}>Received:</strong> {new Date(activeMessage.created_at).toLocaleString()}</span>
              </div>
              <p style={{ fontSize: '1rem', lineHeight: '1.75', color: activeMessage.message ? '#333333' : '#999999', whiteSpace: 'pre-line', fontStyle: activeMessage.message ? 'normal' : 'italic' }}>
                {activeMessage.message || 'No message provided.'}
              </p>
            </div>
            {('read' in activeMessage) && (
              <div style={{ display: 'flex', gap: '12px', padding: '20px 28px', borderTop: '1px solid #F0F0F0' }}>
                <button onClick={() => setRead(activeMessage.id, !activeMessage.read)}
                  data-testid="toggle-read-button"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', border: '1px solid #E0E0E0', borderRadius: '4px', background: '#FFFFFF', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', color: '#111111' }}>
                  <MailOpen size={16} /> Mark as {activeMessage.read ? 'unread' : 'read'}
                </button>
                <button onClick={() => deleteContact(activeMessage.id)}
                  data-testid="modal-delete-button"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', border: 'none', borderRadius: '4px', background: '#cc0000', color: '#FFFFFF', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600' }}>
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionsViewer;
